<script setup>
import { ref } from 'vue'
import { usePage } from '@inertiajs/vue3'
import Button from 'primevue/button'
import Popover from 'primevue/popover'
import ContactsFilterPanel from './ContactsFilterPanel.vue'

const props = defineProps({
    search:           { type: String, default: '' },
    filters:          { type: Object, default: () => ({}) },
    activeFilterCount:{ type: Number, default: 0 },
    allTags:          { type: Array,  default: () => [] },
    tagCounts:        { type: Object, default: () => ({}) },
    reservationForms: { type: Array,  default: () => [] },
})

const emit = defineEmits([
    'update:search',
    'export',
    'toggle-tag',
    'toggle-form',
    'update:date-from',
    'update:date-to',
    'update:activity-from',
    'update:activity-to',
    'reset',
    'create',
])

const canCreate = usePage().props.user.features?.map((f) => f.feature_name).includes('roori_client_manual_create')

const exportPopover = ref()
const filterPopover = ref()
</script>

<template>
    <div class="flex items-center gap-2 flex-shrink-0">

        <!-- Vyhledávání -->
        <div class="relative">
            <i class="pi pi-search absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400 !text-[13px]" />
            <input
                :value="search"
                type="text"
                :placeholder="$t('contacts.search_placeholder')"
                class="pl-8 pr-3 py-1.5 rounded-xl text-[13px] border bg-white dark:bg-neutral-900 border-neutral-200 dark:border-neutral-700 text-neutral-700 dark:text-neutral-200 placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 transition-colors w-48"
                @input="emit('update:search', $event.target.value)"
            />
        </div>

        <!-- Export -->
        <Button
            :label="$t('contacts.btn_export')"
            icon="pi pi-download"
            severity="secondary"
            outlined
            class="!rounded-xl !text-sm !py-1.5"
            @click="exportPopover.toggle($event)"
        />
        <Popover ref="exportPopover" class="!rounded-2xl !p-0 !overflow-hidden">
            <div class="flex flex-col">
                <Button text :label="$t('contacts.export_csv')" icon="pi pi-file"
                    class="!justify-start !rounded-none !text-sm !px-4 !py-2.5 !text-neutral-700 dark:!text-neutral-200 !icon-text-green-600"
                    @click="emit('export', 'csv'); exportPopover.hide()" />
                <Button text :label="$t('contacts.export_xlsx')" icon="pi pi-file-excel"
                    class="!justify-start !rounded-none !text-sm !px-4 !py-2.5 !text-neutral-700 dark:!text-neutral-200"
                    @click="emit('export', 'xlsx'); exportPopover.hide()" />
            </div>
        </Popover>

        <!-- Filtry -->
        <Button
            :severity="activeFilterCount ? undefined : 'secondary'"
            :outlined="!activeFilterCount"
            class="!rounded-xl !text-sm !py-1.5 gap-1.5"
            :class="activeFilterCount
                ? '!bg-blue-50 dark:!bg-blue-950/30 !border-blue-200 dark:!border-blue-800 !text-blue-600 dark:!text-blue-400'
                : ''"
            @click="filterPopover.toggle($event)"
        >
            <i class="pi pi-filter !text-[13px]" />
            {{ $t('contacts.filter_title') }}
            <span
                v-if="activeFilterCount"
                class="w-4 h-4 bg-blue-600 text-white rounded-full text-[13px] font-bold flex items-center justify-center"
            >{{ activeFilterCount }}</span>
        </Button>
        <Popover ref="filterPopover" class="!rounded-2xl !p-0 !overflow-hidden">
            <ContactsFilterPanel
                :all-tags="allTags"
                :tag-counts="tagCounts"
                :reservation-forms="reservationForms"
                :filters="filters"
                @toggle-tag="$emit('toggle-tag', $event)"
                @toggle-form="$emit('toggle-form', $event)"
                @update:date-from="$emit('update:date-from', $event)"
                @update:date-to="$emit('update:date-to', $event)"
                @update:activity-from="$emit('update:activity-from', $event)"
                @update:activity-to="$emit('update:activity-to', $event)"
                @reset="$emit('reset')"
            />
        </Popover>

        <!-- Přidat klienta -->
        <Button
            v-if="canCreate"
            :label="$t('contacts.btn_create')"
            icon="pi pi-plus"
            class="!rounded-xl !text-sm !py-1.5"
            @click="emit('create')"
        />

    </div>
</template>
